import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DailyIframe from "@daily-co/daily-js";
import { Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Embeds Daily Prebuilt for a sorteo broadcast.
 *
 * - When `isAdmin=true`, the user joins as owner (can publish mic+cam).
 * - When `isAdmin=false`, the user joins as listener (read-only).
 *
 * If the sorteo has no Daily room (older sorteo before the feature, or
 * Daily creation failed), shows the tarot-cards fallback overlay.
 */
export function SorteoBroadcast({ sorteoId, token, isAdmin }) {
  const containerRef = useRef(null);
  const callRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | loading | live | error | unavailable
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!sorteoId || !token) return;
    let cancelled = false;

    (async () => {
      setPhase("loading");
      try {
        const { data } = await axios.post(
          `${API}/sorteo/${sorteoId}/daily-token`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (!containerRef.current) return;
        // Always destroy any previous frame first (safety against StrictMode dbl-mount)
        try { callRef.current?.destroy(); } catch { /* noop */ }
        const frame = DailyIframe.createFrame(containerRef.current, {
          showLeaveButton: false,
          showFullscreenButton: true,
          iframeStyle: {
            width: "100%",
            height: "100%",
            border: "0",
            borderRadius: "16px",
            background: "#000",
          },
        });
        callRef.current = frame;
        await frame.join({ url: data.room_url, token: data.token });
        if (!cancelled) setPhase("live");
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 503) {
          setPhase("unavailable");
        } else {
          setPhase("error");
          setErrorMsg(err?.response?.data?.detail || String(err?.message || err));
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        callRef.current?.leave?.();
        callRef.current?.destroy?.();
      } catch { /* noop */ }
      callRef.current = null;
    };
  }, [sorteoId, token]);

  return (
    <div className="relative w-full" style={{ aspectRatio: "16/10", minHeight: "240px" }} data-testid="sorteo-broadcast">
      <div
        ref={containerRef}
        className="absolute inset-0 rounded-2xl overflow-hidden border border-[#d4af37]/30 bg-black"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(0,0,0,0.7), rgba(80,0,80,0.4)), url('https://images.unsplash.com/photo-1601651207237-c50a44ca8024?auto=format&fit=crop&w=1200&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {phase === "loading" && (
        <div className="absolute inset-0 rounded-2xl bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-neutral-200">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
          <span className="text-xs uppercase tracking-widest">Conectando con la transmisión…</span>
        </div>
      )}
      {phase === "unavailable" && (
        <div className="absolute inset-0 rounded-2xl bg-black/50 flex flex-col items-center justify-center gap-2 text-center px-6">
          <span className="text-5xl">🎴</span>
          <span className="text-sm text-[#d4af37] font-semibold uppercase tracking-widest">Sorteo en curso</span>
          <span className="text-xs text-neutral-300">El admin todavía no encendió la transmisión.</span>
        </div>
      )}
      {phase === "error" && (
        <div className="absolute inset-0 rounded-2xl bg-black/70 flex flex-col items-center justify-center gap-2 text-center px-6">
          <AlertCircle className="w-7 h-7 text-red-400" />
          <span className="text-sm text-red-300">No se pudo conectar a la transmisión</span>
          <span className="text-[10px] text-neutral-500 font-mono break-all">{errorMsg}</span>
        </div>
      )}
      {phase === "live" && isAdmin && (
        <div
          data-testid="broadcast-owner-badge"
          className="absolute top-3 left-3 px-3 py-1 bg-red-600 text-white text-[10px] uppercase tracking-widest rounded-full font-bold flex items-center gap-1.5 z-10 pointer-events-none"
        >
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" /> En vivo
        </div>
      )}
    </div>
  );
}
